class BoostCollectionController {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('#product-grid-ajax');
    this.pagination = root.querySelector('#pagination-ajax');
    this.abortController = null;
    this.requestId = null;
    this.filterOptions = [];
    this.state = this.readState();
    this.config = root.dataset;
    this.renderedHandles = this.gridHandles();
    this.handleClicks();
    this.fetchResults('init');
  }

  readState() {
    const params = new URLSearchParams(window.location.search);
    const filters = new Map();

    params.forEach((value, key) => {
      if (key.startsWith('pf_')) {
        filters.set(key, value.split(',').filter(Boolean));
      }
    });

    return {
      page: Math.max(1, Number(params.get('page')) || 1),
      sort: params.get('sort_by') || '',
      filters,
    };
  }

  get isSearch() {
    return this.config.boostContext === 'search';
  }

  buildApiUrl(eventType) {
    // Boost keeps search on its own endpoint; the filter endpoint ignores the q parameter.
    const url = new URL(`https://services.mybcapps.com/bc-sf-filter/${this.isSearch ? 'search' : 'filter'}`);
    const params = url.searchParams;

    params.set('shop', this.config.boostShop);
    if (this.isSearch) {
      params.set('q', this.config.boostSearchTerms || '');
    } else {
      params.set('collection_scope', this.config.boostCollectionId);
    }
    params.set('page', String(this.state.page));
    params.set('limit', this.config.boostPageSize);
    params.set('event_type', this.isSearch && eventType === 'init' ? 'search' : eventType);
    params.set('build_filter_tree', 'true');
    params.set('return_all_currency_fields', 'false');
    params.append('h_options', 'wk_auction');

    if (this.config.boostLocale) params.set('locale', this.config.boostLocale);
    if (this.config.boostCountry) params.set('country', this.config.boostCountry);
    if (this.config.boostCurrency) params.set('currency', this.config.boostCurrency);
    if (this.state.sort) params.set('sort', this.state.sort);

    this.state.filters.forEach((values, key) => {
      values.forEach((value) => params.append(`${key}[]`, this.apiFilterValue(key, value)));
    });

    return url;
  }

  // Boost's stock filter is a boolean field in the API even though its filter tree (and the
  // shareable URL) uses the in-stock / out-of-stock keys.
  apiFilterValue(key, value) {
    if (!key.startsWith('pf_st_')) return value;
    if (value === 'in-stock') return 'true';
    if (value === 'out-of-stock') return 'false';
    return value;
  }

  async fetchResults(eventType, append = false) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setLoading(true);

    try {
      const response = await fetch(this.buildApiUrl(eventType), {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Boost collection request failed: ${response.status}`);
      }

      const data = await response.json();
      if (data.error) throw new Error(data.error);

      this.requestId = data.meta?.rid || null;
      this.filterOptions = data.filter?.options || this.filterOptions;

      const products = this.visibleProducts(data.products || []);

      if (this.canKeepRenderedCards(products, eventType, append)) {
        this.stampProductIds(products);
      } else {
        this.renderProducts(products, append);
        // Fills the Reviews.io placeholders of the freshly rendered cards
        window.renderReviewsIoRatings?.();
      }

      this.grid?.classList.remove('boost-grid-pending');

      this.renderFilters();
      this.renderActiveFilters();
      const total = data.total_product || 0;
      this.renderCount(total);
      this.renderPagination(total);
      this.updateUrl();
      // After the results, so the extra request never delays what the shopper came for.
      this.fetchSpellingCorrection();
      this.renderNoResultRecommendations(total);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Boost collection could not be loaded.', error);
        // Falling back to the Liquid results beats leaving a skeleton on screen.
        this.revealCount();
        this.grid?.classList.remove('boost-grid-pending');
      }
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    this.root.classList.toggle('processing', isLoading);
    this.grid?.setAttribute('aria-busy', String(isLoading));
  }

  visibleProducts(products) {
    return products.filter((product) => {
      const tags = (product.tags || []).map((tag) => String(tag).toLowerCase());
      return !tags.includes('wk_auction');
    });
  }

  // Handles of the cards Liquid rendered, in grid order. Null when the grid was rendered empty.
  gridHandles() {
    const cards = this.grid?.querySelectorAll('.card--product');
    if (!cards?.length) return null;

    return [...cards].map((card) => {
      const href = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
      return href.split('/products/')[1]?.split(/[?#]/)[0] || '';
    });
  }

  // On a plain first load Boost returns exactly the products Liquid already rendered. Rebuilding
  // them would only cause a visible flicker and drop the preloaded images, so the markup is kept.
  canKeepRenderedCards(products, eventType, append) {
    if (eventType !== 'init' || append || !this.renderedHandles) return false;
    if (products.length !== this.renderedHandles.length) return false;

    return products.every((product, index) => product.handle === this.renderedHandles[index]);
  }

  // Click tracking looks cards up by this attribute, so the kept Liquid cards need it as well.
  stampProductIds(products) {
    this.grid.querySelectorAll('.card--product').forEach((card, index) => {
      const id = products[index]?.id;
      if (id != null) card.dataset.boostProductId = String(id);
    });
  }

  renderProducts(visibleProducts, append) {
    const markup = visibleProducts.map((product, index) => this.productCard(product, index)).join('');

    if (append) {
      this.grid.insertAdjacentHTML('beforeend', markup);
      return;
    }

    const emptyLabel = (this.config.boostLabelEmpty || '').replace(
      '__TERMS__',
      this.config.boostSearchTerms || '',
    );
    this.grid.innerHTML = markup || `
      <div class="boost-no-results" data-boost-no-results>
        <h2>${this.escape(emptyLabel)}</h2>
        <div data-boost-no-results-recommendations hidden></div>
      </div>
    `;
  }

  productCard(product, index, forceLazy = false) {
    const images = product.images_info || [];
    const primaryImage = images[0];
    const secondaryImage = this.toBoolean(this.config.boostSecondaryImage) ? images[1] : null;
    const productUrl = this.storefrontUrl(`/products/${product.handle}`);
    const cardClasses = [
      'card',
      'card--product',
      `card--${this.config.boostCardType || 'traditional'}`,
      secondaryImage ? 'on-hover' : '',
    ].filter(Boolean).join(' ');
    const media = secondaryImage
      ? `<div class="card-hover-images">${this.image(primaryImage, product.title, index, forceLazy)}${this.image(secondaryImage, product.title, index, true)}</div>`
      : this.image(primaryImage, product.title, index, forceLazy);

    return `
      <div class="${this.escape(cardClasses)}" data-boost-product-id="${this.escape(product.id)}">
        <a href="${this.escape(productUrl)}" class="card-media p-${this.escape(this.config.boostImagePadding || '0')}" aria-hidden="true" tabindex="-1">
          ${media}
          <div class="card-media-overlay flex ${this.escape(this.config.boostBadgesPosition || '')}">
            ${this.badges(product)}
          </div>
        </a>
        <div class="card-body ${this.escape(this.config.boostTextAlign || '')}">
          ${this.toBoolean(this.config.boostShowVendor) ? `<span class="card-vendor size--tiny">${this.escape(product.vendor || '')}</span>` : ''}
          <h3 class="card-title ${this.escape(this.config.boostTitleClasses || '')}">
            <a href="${this.escape(productUrl)}">${this.escape(product.title || '')}</a>
          </h3>
          ${this.price(product)}
          ${this.rating(product)}
          ${this.colorSwatches(product, productUrl)}
        </div>
        ${this.quickBuy(product, productUrl, primaryImage)}
      </div>
    `;
  }

  // Swatch colours/images come from Liquid (see the boost-swatch-map script in the section),
  // keyed by option value name.
  swatchMap() {
    if (!this.swatchLookup) {
      const element = document.getElementById('boost-swatch-map');
      try {
        this.swatchLookup = element ? JSON.parse(element.textContent) : {};
      } catch (error) {
        this.swatchLookup = {};
      }
    }
    return this.swatchLookup;
  }

  colorSwatches(product, productUrl) {
    if (!this.toBoolean(this.config.boostShowSwatches)) return '';

    const identifiers = (this.config.boostColorIdentifiers || '').toLowerCase();
    if (!identifiers) return '';

    const option = (product.options_with_values || []).find((item) => {
      const label = String(item.label || '').toLowerCase();
      const name = String(item.name || '').toLowerCase();
      return (label && identifiers.includes(label)) || (name && identifiers.includes(name));
    });
    if (!option) return '';

    const lookup = this.swatchMap();
    const swatches = (option.values || []).map((value) => {
      const title = String(value.title ?? value ?? '');
      if (!title) return '';

      const variant = (product.variants || []).find((item) => (item.merged_options || []).includes(`${option.name}:${title}`));
      const url = variant ? `${productUrl}?variant=${variant.id}` : productUrl;
      const swatch = lookup[title.toLowerCase()] || {};
      const color = swatch.color || this.handleize(title);
      const visual = swatch.image
        ? `<img src="${this.escape(swatch.image)}" alt="${this.escape(title)}" class="color" style="--color: ${this.escape(color)};" width="16" height="16" loading="lazy" decoding="async">`
        : `<span class="color" style="--color: ${this.escape(color)};"></span>`;

      return `<a class="swatch" href="${this.escape(url)}">${visual}<span class="visually-hidden">${this.escape(title)}</span></a>`;
    }).join('');

    if (!swatches) return '';

    return `
      <div class="mt-12 swatches flex" style="gap: 0.6rem;">
        <span class="visually-hidden">${this.escape(this.config.boostLabelAvailableIn)}</span>
        ${swatches}
      </div>
    `;
  }

  // Reviews.io fills these placeholders with stars. It matches products by handle, variant SKU
  // or variant id, so all three are passed in data-sku like Reviews.io's own snippet does.
  rating(product) {
    if (!this.toBoolean(this.config.boostShowRating)) return '';

    const variants = product.variants || [];
    const identifiers = [
      product.handle,
      ...variants.map((variant) => variant.sku),
      ...variants.map((variant) => variant.id),
    ].filter((value) => value !== null && value !== undefined && value !== '');

    if (!identifiers.length) return '';

    return `<div class="ruk_rating_snippet mt-8" data-sku="${this.escape(identifiers.join(';'))}"></div>`;
  }

  image(image, productTitle, index, forceLazy = false) {
    if (!image?.src) {
      return `<figure class="media ${this.escape(this.config.boostFillRule)} ${this.escape(this.config.boostAspectRatio)} media--empty"></figure>`;
    }

    const widths = [280, 390, 540, 768, 1080].filter((width) => width <= image.width);
    const srcset = widths.map((width) => `${this.imageUrl(image.src, width)} ${width}w`).join(', ');
    const lazy = forceLazy || index >= 4;

    return `
      <figure class="media ${this.escape(this.config.boostFillRule)} ${this.escape(this.config.boostAspectRatio)}">
        <img
          src="${this.escape(this.imageUrl(image.src, 768))}"
          ${srcset ? `srcset="${this.escape(srcset)}"` : ''}
          sizes="(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw"
          alt="${this.escape(image.alt || productTitle || '')}"
          width="${this.escape(image.width || 768)}"
          height="${this.escape(image.height || 768)}"
          ${lazy ? 'loading="lazy" fetchpriority="low"' : 'fetchpriority="high"'}
          decoding="async">
      </figure>
    `;
  }

  imageUrl(source, width) {
    if (!source) return '';
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  }

  badges(product) {
    if (!this.toBoolean(this.config.boostShowBadges)) return '';

    const tags = (product.tags || []).map((tag) => String(tag));
    const lowerTags = tags.map((tag) => tag.toLowerCase());
    const badges = [];
    const price = Number(product.price_min);
    const compareAt = Number(product.compare_at_price_max);

    if (compareAt > price) badges.push(this.badge('sale', this.config.boostLabelSale));
    if (lowerTags.includes('preorder')) badges.push(this.badge('preorder', this.config.boostLabelPreorder));
    if (lowerTags.includes('new')) badges.push(this.badge('new', this.config.boostLabelNew));
    if (!product.available) badges.push(this.badge('soldout', this.config.boostLabelSoldOut));

    (this.config.boostCustomBadges || '').split(';').forEach((rule) => {
      const label = rule.split(':')[0]?.trim();
      if (label && tags.includes(label)) {
        badges.push(this.badge(`custom-${this.handleize(label)}`, label));
      }
    });

    return badges.length ? `<div class="badges">${badges.join('')}</div>` : '';
  }

  badge(className, label) {
    return `<div class="badge ${this.escape(className)}" aria-label="${this.escape(label)}">${this.escape(label)}</div>`;
  }

  price(product) {
    const price = Number(product.price_min || 0);
    const maxPrice = Number(product.price_max || price);
    const compareAt = Number(product.compare_at_price_max || 0);
    const onSale = compareAt > price;
    const unavailable = !product.available;
    let formattedPrice = this.money(price);

    if (maxPrice > price) {
      formattedPrice = this.config.boostLabelFromPrice.replace('[price]', formattedPrice);
    }

    return `
      <div class="price mt-8 ${this.escape(this.config.boostPriceClasses || '')}${unavailable ? ' price--sold-out' : ''}${onSale ? ' price--on-sale' : ''}">
        <div class="flex">
          <div class="price__regular">
            <span class="visually-hidden">${this.escape(this.config.boostLabelRegularPrice)}</span>
            <span class="price-item price-item--regular">${this.escape(formattedPrice)}</span>
          </div>
          <div class="price__sale">
            <span class="visually-hidden">${this.escape(this.config.boostLabelRegularPrice)}</span>
            <span><s class="price-item price-item--regular style--subdued">${this.escape(this.money(compareAt))}</s></span>
            <span class="visually-hidden">${this.escape(this.config.boostLabelSalePrice)}</span>
            <span class="price-item price-item--sale price-item--last">${this.escape(formattedPrice)}</span>
          </div>
        </div>
      </div>
    `;
  }

  quickBuy(product, productUrl, image) {
    if (!this.toBoolean(this.config.boostQuickBuy)) return '';

    const variants = product.variants || [];
    const availableVariant = variants.find((variant) => variant.available);
    const classes = `js quick-buy-or-add ${this.config.boostTextAlign || ''}`;
    const buttonStyle = this.config.boostQuickBuyStyle || 'primary';

    if (this.config.boostQuickBuyMode === 'quick_view' || variants.length > 1) {
      return `
        <quick-buy data-product="${this.escape(product.id)}" data-url="${this.escape(productUrl)}" class="${this.escape(classes)} quick-buy">
          <a class="btn size--tiny btn--${this.escape(buttonStyle)} quick-buy-link btn--full" data-product-handle="${this.escape(product.handle)}" href="#modal-${this.escape(product.id)}-product" aria-haspopup="dialog">
            <span class="quick-buy-text">${this.escape(this.config.boostLabelQuickBuy)}</span>
          </a>
        </quick-buy>
      `;
    }

    if (availableVariant) {
      return `
        <quick-add data-variant-id="${this.escape(availableVariant.id)}" data-product-id="${this.escape(product.id)}" data-product-image="${this.escape(this.imageUrl(image?.src || '', 120))}" class="${this.escape(classes)} quick-add">
          <button type="button" class="btn size--tiny btn--${this.escape(buttonStyle)} btn--full">
            <span class="quick-buy-text">${this.escape(this.config.boostLabelQuickAdd)}</span>
          </button>
        </quick-add>
      `;
    }

    return `
      <div class="${this.escape(classes)} quick-add-unavailable">
        <button class="btn size--tiny btn--${this.escape(buttonStyle)} btn--full" disabled>${this.escape(this.config.boostLabelUnavailable)}</button>
      </div>
    `;
  }

  renderFilters() {
    const visibleOptions = this.filterOptions.filter((option) => this.hasFilterValues(option));

    document.querySelectorAll('boost-facets-form .filter-form').forEach((container, containerIndex) => {
      const currentGroups = new Map(
        Array.from(container.children).map((child) => [child.dataset.boostFilterGroup, child]),
      );

      const nextGroups = visibleOptions.map((option) => {
        const existing = currentGroups.get(option.filterOptionId);

        // Keep the live price slider node instead of building a new one: re-creating it would
        // cancel an in-progress drag, lose the shopper's values and leave a stray
        // PriceRangeSlider instance behind.
        if (existing && option.filterType === 'price') return existing;

        const template = document.createElement('div');
        template.innerHTML = this.filterGroup(option, containerIndex);
        const group = template.firstElementChild;
        if (existing) group.open = existing.open;
        return group;
      });

      const keptGroups = new Set(nextGroups);
      Array.from(container.children).forEach((child) => {
        if (!keptGroups.has(child)) child.remove();
      });

      nextGroups.forEach((group, index) => {
        if (group.parentNode === container) return;
        container.insertBefore(group, container.children[index] || null);
      });
    });
  }

  hasFilterValues(option) {
    if (!option.filterOptionId || option.status === 'inactive') return false;
    if (option.filterType === 'price') return this.priceBounds(option) !== null;
    return this.optionValues(option).length > 0;
  }

  // Boost returns every configured value, including ones with no products in the current
  // result set. Hide those so shoppers only see filters they can actually use.
  optionValues(option) {
    const selected = this.state.filters.get(option.filterOptionId) || [];
    const values = Array.isArray(option.values) ? option.values : [];
    return values.filter((value) => Number(value.doc_count) > 0 || selected.includes(String(value.key)));
  }

  priceBounds(option) {
    const min = Math.floor(Number(option.values?.min));
    const max = Math.ceil(Number(option.values?.max));
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
    return { min, max };
  }

  filterGroup(option, containerIndex) {
    const id = option.filterOptionId;
    const groupAttributes = `data-boost-filter-group="${this.escape(id)}" data-boost-filter-type="${this.escape(option.filterType)}" ${option.isCollapsePC ? '' : 'open'}`;

    if (option.filterType === 'price') {
      const bounds = this.priceBounds(option);
      const selected = this.state.filters.get(id)?.[0]?.split(':') || [];
      const currentMin = this.clampPrice(selected[0], bounds, bounds.min);
      const currentMax = this.clampPrice(selected[1], bounds, bounds.max);
      const inputId = `boost-${this.handleize(id)}-${containerIndex}`;
      const symbol = this.config.boostCurrencySymbol || '';

      // Same markup as snippets/facets.liquid so the theme's price-range-slider element
      // and its styles work unchanged.
      return `
        <details class="filter-group" ${groupAttributes}>
          <summary><span>${this.escape(option.label)}</span><span aria-hidden="true">⌄</span></summary>
          <div class="content pt-12 pb-24">
            <fieldset>
              <legend class="visually-hidden">${this.escape(option.label)}</legend>
              <price-range-slider
                class="price-range-slider"
                data-boost-price-key="${this.escape(id)}"
                data-min="${bounds.min}"
                data-max="${bounds.max}"
                data-min-value="${currentMin}"
                data-max-value="${currentMax}">
                <div class="slider-container">
                  <div class="range"></div>
                  <div class="thumb min-value" data-thumb-min tabindex="0" role="slider"></div>
                  <div class="thumb max-value" data-thumb-max tabindex="0" role="slider"></div>
                </div>
                <div class="inputs">
                  <div class="price-range-input-wrapper">
                    <label for="${inputId}-from" class="currency"><span class="visually-hidden">${this.escape(this.config.boostLabelFrom)}</span>${this.escape(symbol)}</label>
                    <input id="${inputId}-from" class="price-range-input" type="number" inputmode="decimal" data-min data-boost-price-min="${this.escape(id)}" min="${bounds.min}" max="${bounds.max}" value="${currentMin}">
                  </div>
                  <div class="price-range__horizontal-divider" aria-hidden="true">-</div>
                  <div class="price-range-input-wrapper">
                    <label for="${inputId}-to" class="currency"><span class="visually-hidden">${this.escape(this.config.boostLabelTo)}</span>${this.escape(symbol)}</label>
                    <input id="${inputId}-to" class="price-range-input" type="number" inputmode="decimal" data-max data-boost-price-max="${this.escape(id)}" min="${bounds.min}" max="${bounds.max}" value="${currentMax}">
                  </div>
                </div>
              </price-range-slider>
            </fieldset>
          </div>
        </details>
      `;
    }

    const style = this.filterStyle(option);
    const selectedValues = this.state.filters.get(id) || [];
    const options = this.optionValues(option).map((value) => {
      const key = String(value.key);
      const label = this.valueLabel(option, value);
      // Counts would break the compact button/swatch layouts, just like in the theme's own facets.
      const count = style === 'default'
        ? ` <span class="style--subdued">(${this.escape(value.doc_count || 0)})</span>`
        : '';

      return `
        <li class="filter-option">
          <label class="filter-type-${this.escape(style)}">
            <input class="${this.escape(this.filterInputClass(style))}" type="checkbox" name="${this.escape(id)}" value="${this.escape(key)}" data-key-value="${this.escape(id)}:${this.escape(key)}" ${selectedValues.includes(key) ? 'checked' : ''}>
            ${style === 'color' ? `<span class="swatch">${this.filterSwatch(label)}</span>` : ''}
            <span>${this.escape(label)}${count}</span>
          </label>
        </li>
      `;
    }).join('');

    return `
      <details class="filter-group" ${groupAttributes}>
        <summary><span>${this.escape(option.label)}</span><span aria-hidden="true">⌄</span></summary>
        <div class="content pt-12 pb-24">
          <fieldset>
            <legend class="visually-hidden">${this.escape(option.label)}</legend>
            <ul class="filter-options-list ${this.escape(style)}${style === 'as-button' ? ' grid grid-cols-4' : ''}" style="gap: 8px;">${options}</ul>
          </fieldset>
        </div>
      </details>
    `;
  }

  // Mirrors snippets/facets.liquid: colour filters render as swatches and the filters listed in
  // products.facets.filters_as_buttons_identifiers render as a button grid, so JS-rendered
  // filters look the same as the theme's own (for example on the search page).
  filterStyle(option) {
    const label = String(option.label || '').toLowerCase();
    if (!label) return 'default';

    const colorIdentifiers = (this.config.boostColorIdentifiers || '').toLowerCase();
    const buttonIdentifiers = (this.config.boostButtonIdentifiers || '').toLowerCase();

    if (this.toBoolean(this.config.boostFacetSwatches) && colorIdentifiers.includes(label)) return 'color';
    if (buttonIdentifiers.includes(label)) return 'as-button';
    return 'default';
  }

  filterInputClass(style) {
    if (style === 'color') return 'for-swatch';
    if (style === 'as-button') return 'as-button';
    return 'checkbox';
  }

  // Same output as snippets/color-swatches.liquid, using the Liquid-built swatch lookup.
  filterSwatch(label) {
    const swatch = this.swatchMap()[String(label).toLowerCase()] || {};
    const color = swatch.color || this.handleize(label);

    if (swatch.image) {
      return `<img src="${this.escape(swatch.image)}" alt="${this.escape(label)}" class="color" style="--color: ${this.escape(color)};" width="28" height="28" loading="lazy" decoding="async">`;
    }

    return `<span class="color" style="--color: ${this.escape(color)};"></span>`;
  }

  // Rating values come back as raw keys like "4.0-*", so show stars instead.
  valueLabel(option, value) {
    const from = Math.round(Number(value.from));
    if (option.displayType === 'rating' && Number.isFinite(from)) {
      return `${'★'.repeat(from)}${'☆'.repeat(Math.max(0, 5 - from))}`;
    }
    return value.label || value.displayName || String(value.key);
  }

  clampPrice(value, bounds, fallback) {
    const number = Math.round(Number(value));
    if (!Number.isFinite(number)) return fallback;
    return Math.min(Math.max(number, bounds.min), bounds.max);
  }

  renderActiveFilters() {
    const options = new Map(this.filterOptions.map((option) => [option.filterOptionId, option]));
    const items = [];

    this.state.filters.forEach((values, key) => {
      const option = options.get(key);
      values.forEach((value) => {
        items.push(`
          <button type="button" class="btn btn--with-icon size--tiny" data-boost-remove-filter="${this.escape(key)}" data-boost-remove-value="${this.escape(value)}">
            <span>${this.escape(option?.label || key)}: ${this.escape(this.filterValueLabel(option, value))}</span>
            <span aria-hidden="true">×</span>
          </button>
        `);
      });
    });

    this.root.querySelectorAll('#current-facets-ajax').forEach((container) => {
      container.innerHTML = items.join('');
    });
  }

  filterValueLabel(option, value) {
    if (option?.filterType === 'price') {
      const [min, max] = String(value).split(':');
      return `${this.money(Number(min))} – ${this.money(Number(max))}`;
    }

    const values = Array.isArray(option?.values) ? option.values : [];
    const match = values.find((item) => String(item.key) === String(value));
    return match ? this.valueLabel(option, match) : value;
  }

  renderCount(total) {
    const strings = window.themeStrings || {};
    const template = total === 1
      ? this.config.boostLabelCountOne || strings.product_count_simple_one
      : this.config.boostLabelCountOther || strings.product_count_simple_other;
    const text = template ? template.replace('[count]', String(total)) : String(total);

    this.root.querySelectorAll('#products-count-ajax').forEach((count) => {
      count.textContent = text;
    });
    this.revealCount();

    const applyCount = document.getElementById('apply-count');
    if (applyCount) applyCount.textContent = text;
  }

  async boostNoResultSettings() {
    // Boost exposes the merchant-managed no-result lists after its app script initializes.
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const searchSettings = window.boostISWModule
        ?.instantSearchService
        ?.instantSearchAPI
        ?.TAEAppConfig
        ?.templateSettings
        ?.searchSettings;

      if (searchSettings) {
        const noResult = searchSettings.suggestionNoResult || {};
        const blocks = searchSettings.searchPanelBlocks || {};
        return {
          searches: noResult.search_terms || {
            status: blocks.searchTermSuggestions?.active,
            data: blocks.searchTermSuggestions?.searchTermList,
            label: blocks.searchTermSuggestions?.label,
          },
          products: noResult.products || {
            status: blocks.mostPopularProducts?.active,
            data: blocks.mostPopularProducts?.productList,
            label: blocks.mostPopularProducts?.label,
          },
        };
      }

      await new Promise((resolve) => window.setTimeout(resolve, 100));
    }

    return null;
  }

  async renderNoResultRecommendations(total) {
    const container = this.root.querySelector('[data-boost-no-results-recommendations]');
    if (!this.isSearch || total > 0 || !container || this.noResultRecommendationsRequested) return;
    this.noResultRecommendationsRequested = true;

    const settings = await this.boostNoResultSettings();
    if (!settings) return;

    const searchTerms = settings.searches?.status === false
      ? []
      : (settings.searches?.data || []).filter(Boolean);
    const productHandles = settings.products?.status === false
      ? []
      : (settings.products?.data || []).filter(Boolean);

    const searchesMarkup = searchTerms.length
      ? `
        <section class="boost-no-results__section" aria-labelledby="boost-popular-searches-heading">
          <h2 id="boost-popular-searches-heading" class="h4">
            ${this.escape(settings.searches?.label || this.config.boostLabelPopularSearches)}
          </h2>
          <ul class="boost-no-results__searches" role="list">
            ${searchTerms.map((term) => `
              <li><a class="link" href="${this.storefrontUrl(`/search?q=${encodeURIComponent(term)}`)}">${this.escape(term)}</a></li>
            `).join('')}
          </ul>
        </section>
      `
      : '';

    container.innerHTML = searchesMarkup;
    container.hidden = !searchesMarkup;

    if (!productHandles.length) return;

    const url = new URL('https://services.mybcapps.com/bc-sf-filter/search/products');
    const params = url.searchParams;
    params.set('shop', this.config.boostShop);
    params.set('return_all_currency_fields', 'false');
    params.set('q', this.config.boostSearchTerms || '');
    if (this.config.boostLocale) params.set('locale', this.config.boostLocale);
    if (this.config.boostCountry) params.set('country', this.config.boostCountry);
    if (this.config.boostCurrency) params.set('currency', this.config.boostCurrency);
    productHandles.forEach((handle) => params.append('handle', handle));

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const data = await response.json();
      const products = this.visibleProducts(Array.isArray(data) ? data : (data.products || []));
      if (!products.length) return;

      const gridClasses = Array.from(this.grid.classList)
        .filter((name) => name === 'grid'
          || name === 'sm-gap'
          || name.startsWith('grid-cols-')
          || name.startsWith('sm-grid-cols-')
          || name.startsWith('md-grid-cols-'))
        .join(' ');

      container.insertAdjacentHTML('beforeend', `
        <section class="boost-no-results__section boost-no-results__products" aria-labelledby="boost-popular-products-heading">
          <h2 id="boost-popular-products-heading" class="h4">
            ${this.escape(settings.products?.label || this.config.boostLabelPopularProducts)}
          </h2>
          <div class="${this.escape(gridClasses)}">
            ${products.map((product, index) => this.productCard(product, index, true)).join('')}
          </div>
        </section>
      `);
      container.hidden = false;
      window.renderReviewsIoRatings?.();
    } catch (error) {
      // Keep the useful empty-state message and search terms if recommendations fail.
    }
  }

  // The search endpoint corrects typos silently and never returns the correction, so the "did you
  // mean" text comes from the lighter suggest endpoint instead. It only depends on the search term,
  // which JavaScript never changes, so one request per page load is enough.
  async fetchSpellingCorrection() {
    const container = this.root.querySelector('[data-boost-did-you-mean]');
    const term = this.config.boostSearchTerms;
    if (!this.isSearch || !container || !term || this.correctionRequested) return;
    this.correctionRequested = true;

    const url = new URL('https://services.mybcapps.com/bc-sf-filter/search/suggest');
    const params = url.searchParams;
    params.set('shop', this.config.boostShop);
    params.set('q', term);
    // Only the correction is used, so ask for as little else as the endpoint allows.
    params.set('product_limit', '1');
    params.set('collection_limit', '0');
    params.set('suggestion_limit', '0');
    params.set('page_limit', '0');
    params.set('return_all_currency_fields', 'false');
    params.set('re_run_if_typo', 'true');
    if (this.config.boostLocale) params.set('locale', this.config.boostLocale);

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const data = await response.json();
      const corrected = data.did_you_mean?.[0] || data.correct_query;
      if (!corrected || corrected === term) return;

      const [before, after] = (this.config.boostLabelDidYouMean || '').split('__TERMS__');
      // The current path already carries the locale prefix of the search page.
      const href = `${window.location.pathname}?q=${encodeURIComponent(corrected)}`;
      container.innerHTML = `${this.escape(before || '')}<a class="link" href="${href}">${this.escape(corrected)}</a>${this.escape(after || '')}`;
      container.hidden = false;
    } catch (error) {
      // A missing suggestion is not worth an error message for the shopper.
    }
  }

  // Also called after a failed request: a stale count is better than a permanently hidden one.
  revealCount() {
    this.root.querySelectorAll('.boost-count-pending').forEach((count) => {
      count.classList.remove('boost-count-pending');
    });
  }

  renderPagination(total) {
    const pageSize = Number(this.config.boostPageSize);
    const totalPages = Math.ceil(total / pageSize);

    if (totalPages <= 1) {
      this.pagination.innerHTML = '';
      return;
    }

    if (this.config.boostPagination === 'button' || this.config.boostPagination === 'infinite') {
      this.pagination.innerHTML = this.state.page < totalPages
        ? `
          <div class="pagination mt-48 mb-48">
            <button type="button" class="btn btn--secondary size--small" data-boost-load-more>
              <span>${this.escape(this.config.boostLabelLoadMore)}</span>
              <div class="loading-overlay hidden">
                <div class="loading-overlay__spinner">
                  <svg aria-hidden="true" focusable="false" role="presentation" style="width: 15px; height: 15px;" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </div>
            </button>
          </div>
        `
        : `<div class="pagination mt-48 mb-48"><p class="end-of-items style--subdued size--tiny">${this.escape(this.config.boostLabelAllLoaded)}</p></div>`;

      if (this.config.boostPagination === 'infinite') this.observeLoadMore();
      return;
    }

    const parts = this.paginationParts(totalPages);

    // Same structure and icons as snippets/pagination.liquid so component-pagination.css applies.
    this.pagination.innerHTML = `
      <nav class="pagination mt-48 mb-48 ${parts.length > 5 ? 'wide-parts' : ''} size--tiny" role="navigation" aria-label="${this.escape(this.config.boostLabelPagination)}">
        ${this.paginationArrow('prev', this.state.page - 1, this.state.page > 1)}
        <div class="pagination-parts">${parts.map((part) => this.paginationPart(part)).join('')}</div>
        ${this.paginationArrow('next', this.state.page + 1, this.state.page < totalPages)}
      </nav>
    `;
  }

  // Mirrors Shopify's paginate.parts: first and last page always visible, a window around the
  // current page and an ellipsis for the gaps.
  paginationParts(totalPages) {
    const current = this.state.page;
    const windowStart = Math.max(2, current - 2);
    const windowEnd = Math.min(totalPages - 1, current + 2);
    const parts = [1];

    if (windowStart > 2) parts.push('…');
    for (let page = windowStart; page <= windowEnd; page += 1) parts.push(page);
    if (windowEnd < totalPages - 1) parts.push('…');
    if (totalPages > 1) parts.push(totalPages);

    return parts;
  }

  paginationPart(part) {
    if (part === '…') {
      return '<span class="pagination-item dot-notation"><span>…</span></span>';
    }

    const label = this.escape((this.config.boostLabelPage || '').replace('[number]', part));

    if (part === this.state.page) {
      return `<span class="pagination-item current-page" aria-current="page" aria-label="${label}"><span>${part}</span></span>`;
    }

    return `<a href="${this.escape(this.pageUrl(part))}" class="pagination-item page-link" data-boost-page="${part}" aria-label="${label}"><span>${part}</span></a>`;
  }

  paginationArrow(direction, page, isEnabled) {
    const icon = direction === 'prev'
      ? '<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-left"><polyline points="12 6 8 10 12 14"/></svg>'
      : '<svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-left"><polyline points="8 14 12 10 8 6"/></svg>';
    const classes = `pagination-item arrow-link arrow-link--${direction}`;

    if (!isEnabled) return `<span class="${classes} disabled">${icon}</span>`;

    const label = direction === 'prev' ? this.config.boostLabelPrevious : this.config.boostLabelNext;
    return `<a href="${this.escape(this.pageUrl(page))}" class="${classes}" data-boost-page="${page}" aria-label="${this.escape(label)}">${icon}</a>`;
  }

  observeLoadMore() {
    const button = this.pagination.querySelector('[data-boost-load-more]');
    if (!button || !('IntersectionObserver' in window)) return;

    this.loadMoreObserver?.disconnect();
    this.loadMoreObserver = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        this.loadMoreObserver.disconnect();
        this.loadMore();
      }
    }, { rootMargin: '300px' });
    this.loadMoreObserver.observe(button);
  }

  updateFilter(input) {
    const key = input.name;
    const selected = new Set(this.state.filters.get(key) || []);

    input.checked ? selected.add(input.value) : selected.delete(input.value);
    if (selected.size) {
      this.state.filters.set(key, [...selected]);
    } else {
      this.state.filters.delete(key);
    }

    this.state.page = 1;
    this.fetchResults('filter');
  }

  updatePrice(input) {
    const key = input.dataset.boostPriceMin || input.dataset.boostPriceMax;
    const slider = input.closest('[data-boost-price-key]');
    const min = Number(slider?.querySelector('[data-boost-price-min]')?.value);
    const max = Number(slider?.querySelector('[data-boost-price-max]')?.value);
    const bounds = { min: Number(slider?.dataset.min), max: Number(slider?.dataset.max) };
    const isFullRange = min <= bounds.min && max >= bounds.max;

    if (Number.isFinite(min) && Number.isFinite(max) && !isFullRange) {
      this.state.filters.set(key, [`${min}:${max}`]);
    } else {
      this.state.filters.delete(key);
    }

    this.state.page = 1;
    clearTimeout(this.priceTimer);
    this.priceTimer = setTimeout(() => this.fetchResults('filter'), 350);
  }

  updateSort(value) {
    this.state.sort = value;
    this.state.page = 1;
    this.root.querySelectorAll('boost-sort-by select, boost-sort-by input[type="radio"]').forEach((input) => {
      input.checked = input.type === 'radio' ? input.value === value : input.checked;
      if (input.tagName === 'SELECT') input.value = value;
    });
    this.fetchResults('sort');
  }

  removeFilter(key, value) {
    const selected = (this.state.filters.get(key) || []).filter((item) => item !== value);
    selected.length ? this.state.filters.set(key, selected) : this.state.filters.delete(key);
    this.resetPriceSliders(key);
    this.state.page = 1;
    this.fetchResults('filter');
  }

  // The slider keeps its own DOM between renders, so clearing a price chip has to reset it.
  resetPriceSliders(key) {
    document.querySelectorAll(`[data-boost-price-key="${CSS.escape(key)}"]`).forEach((slider) => {
      slider.reset?.();
    });
  }

  loadMore() {
    this.state.page += 1;
    this.fetchResults('page', true);
  }

  goToPage(page) {
    this.state.page = page;
    this.fetchResults('page');
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  handleClicks() {
    this.root.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-boost-remove-filter]');
      if (removeButton) {
        this.removeFilter(removeButton.dataset.boostRemoveFilter, removeButton.dataset.boostRemoveValue);
        return;
      }

      const pageLink = event.target.closest('[data-boost-page]');
      if (pageLink) {
        event.preventDefault();
        this.goToPage(Number(pageLink.dataset.boostPage));
        return;
      }

      if (event.target.closest('[data-boost-load-more]')) {
        this.loadMore();
        return;
      }

      const card = event.target.closest('[data-boost-product-id]');
      if (!card) return;

      let userAction = 'view_product';
      if (event.target.closest('quick-add')) userAction = 'add_to_cart';
      if (event.target.closest('quick-buy')) userAction = 'quick_view';
      this.track(userAction, card.dataset.boostProductId);
    });
  }

  track(userAction, productId) {
    const sessionId = this.sessionId();
    const body = {
      tid: this.config.boostShop,
      qs: this.isSearch
        ? `q=${encodeURIComponent(this.config.boostSearchTerms || '')}`
        : `collection_scope=${this.config.boostCollectionId}`,
      eid: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
      rid: this.requestId,
      pid: productId,
      t: new Date().toISOString(),
      u: userAction,
      a: this.isSearch ? 'search' : 'filter',
      r: document.referrer,
      sid: sessionId,
      pg: this.isSearch ? 'search' : 'collection',
    };

    fetch('https://lambda.mybcapps.com/e', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  }

  sessionId() {
    const existing = localStorage.getItem('boostSdSessionId');
    if (existing) return existing;
    const sessionId = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    localStorage.setItem('boostSdSessionId', sessionId);
    return sessionId;
  }

  updateUrl() {
    const url = new URL(window.location.href);
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith('pf_') || key === 'page' || key === 'sort_by') {
        url.searchParams.delete(key);
      }
    });

    this.state.filters.forEach((values, key) => url.searchParams.set(key, values.join(',')));
    if (this.state.sort) url.searchParams.set('sort_by', this.state.sort);
    if (this.state.page > 1) url.searchParams.set('page', String(this.state.page));
    window.history.replaceState({}, '', url);
  }

  pageUrl(page) {
    const url = new URL(window.location.href);
    page > 1 ? url.searchParams.set('page', String(page)) : url.searchParams.delete('page');
    return `${url.pathname}${url.search}`;
  }

  storefrontUrl(path) {
    const root = window.routes?.root || '/';
    return `${root === '/' ? '' : root.replace(/\/$/, '')}${path}`;
  }

  money(amount) {
    return new Intl.NumberFormat(this.config.boostLocale || undefined, {
      style: 'currency',
      currency: this.config.boostCurrency || 'EUR',
    }).format(amount || 0);
  }

  handleize(value) {
    return String(value).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  toBoolean(value) {
    return value === 'true';
  }

  escape(value) {
    const element = document.createElement('div');
    element.textContent = value == null ? '' : String(value);
    return element.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }
}

class BoostFacetsForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.addEventListener('submit', (event) => event.preventDefault());
    this.addEventListener('change', (event) => {
      const controller = window.boostCollectionController;
      if (!controller) return;
      if (event.target.matches('input[type="checkbox"][name^="pf_"]')) controller.updateFilter(event.target);
      if (event.target.matches('[data-boost-price-min], [data-boost-price-max]')) controller.updatePrice(event.target);
    });
  }
}

class BoostSortBy extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.addEventListener('change', (event) => {
      window.boostCollectionController?.updateSort(event.target.value);
    });
  }
}

if (!customElements.get('boost-facets-form')) customElements.define('boost-facets-form', BoostFacetsForm);
if (!customElements.get('boost-sort-by')) customElements.define('boost-sort-by', BoostSortBy);

const boostCollectionRoot = document.querySelector('[data-boost-collection-grid]');
if (boostCollectionRoot) {
  window.boostCollectionController = new BoostCollectionController(boostCollectionRoot);
}
